import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useApiCaller } from '../utils/ApiUtilities';
import { getLoginUrl } from '../utils/UrlUtilities';
import { constructLoginPayload } from '../utils/PayloadUtils';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { getUserData } from '../utils/TokenUtilities';
import GoogleAuthButton from './GoogleAuthButton';

export default function Login() {
    const { loginState } = useUser();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    // While the login request is in flight we show an inline "Identity Verifying" bar instead of
    // the app-wide top-corner spinner (which is suppressed for this call via `spinner: false`).
    const [verifying, setVerifying] = useState(false);
    const navigate = useNavigate();

    // Post-login destination: honor an internal ?redirect= path (e.g. a clicked notice), else /home.
    // Only same-site absolute paths are allowed (guards against open-redirects).
    const safeRedirect = () => {
        try {
            const r = new URLSearchParams(window.location.search).get('redirect');
            if (r && r.startsWith('/') && !r.startsWith('//')) return r;
        } catch { /* ignore */ }
        return '/home';
    };

    const isEmailValid = email => /^\S+@\S+\.\S+$/.test(email);
    const isPasswordValid = password =>
        new RegExp(`^(?=.*[A-Z])(?=.*\\d)(?=.*[!@#$%^&*()_+\\-=[\\]{};:'",.<>/?]).{8,}$`).test(password);

    const isFormValid = () => isEmailValid(email) && isPasswordValid(password);

    const CallApi = useApiCaller();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isFormValid() || verifying) return;
        const loginPayload = constructLoginPayload(email, password);
        const loginUrl = getLoginUrl();

        setVerifying(true);
        let loginResponse;
        try {
            loginResponse = await CallApi({
                url: loginUrl,
                method: 'POST',
                payload: loginPayload,
                token: false,
                spinner: false, // inline "Identity Verifying" bar replaces the global spinner here
            });
        } finally {
            // Keep the bar up through a successful navigate (unmount clears it); drop it on failure.
            if (!loginResponse?.success) setVerifying(false);
        }

        if (loginResponse.success) {
            const userData = getUserData(loginResponse.data.jwt_token);
            loginState({
                userId: userData?.user_id,
                email: userData?.user_email,
                name: userData?.user_name,
                user_role: userData?.user_role,
                jwt_token: loginResponse.data.jwt_token,
                refresh_token: loginResponse.data.refresh_token
            });
            navigate(safeRedirect());
        } else {
            console.error('Login Error Message::', loginResponse.message);
        }
    };

    return (
        <div className="container d-flex justify-content-center align-items-start">
            <div className="card shadow-sm my-5" style={{ maxWidth: 460, width: '100%', border: '1px solid #ede9fe', borderRadius: '0.9rem', overflow: 'hidden' }}>
                <div style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff', padding: '1.4rem 1.25rem', textAlign: 'center' }}>
                    <h3 className="mb-1 fw-bold">Welcome back</h3>
                    <div style={{ opacity: 0.9, fontSize: '0.9rem' }}>Log in to continue to Kobra Prep</div>
                </div>
                <div className="card-body p-4">
                    <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                        <label className="form-label fw-semibold" style={{ color: '#5b21b6' }}>Email address <span className="text-danger">*</span></label>
                        <input
                            type="email"
                            className={`form-control ${email && !isEmailValid(email) ? 'is-invalid' : isEmailValid(email) ? 'is-valid' : ''}`}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                        {!isEmailValid(email) && email && (
                            <div className="invalid-feedback">Enter a valid email.</div>
                        )}
                        </div>

                        <div className="mb-3">
                        <label className="form-label fw-semibold" style={{ color: '#5b21b6' }}>Password <span className="text-danger">*</span></label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                className={`form-control ${password && !isPasswordValid(password) ? 'is-invalid' : ''}`}
                                style={{ paddingRight: '2.5rem' }}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword((s) => !s)}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                title={showPassword ? 'Hide password' : 'Show password'}
                                style={{ position: 'absolute', right: '0.6rem', top: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: '#7c3aed', padding: 0, zIndex: 3 }}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                        {!isPasswordValid(password) && password && (
                            <div className="invalid-feedback d-block">
                            Password must be at least 8 characters long and contain at least one uppercase letter, one number, and one special character.<br />
                                <strong>Allowed special characters:</strong> ! @ # $ % ^ &amp; * ( ) _ + - = [ ] {'{'} {'}'} ; : ' " , . &lt; &gt; / ?
                            </div>
                        )}
                        </div>

                        <button
                            type="submit"
                            className="btn w-100 text-white fw-semibold"
                            style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', border: 'none', boxShadow: '0 3px 0 #4c1d95, 0 4px 6px rgba(0,0,0,0.2)' }}
                            disabled={!isFormValid() || verifying}
                        >
                        {verifying ? 'Verifying…' : 'Login'}
                        </button>

                        {verifying && (
                            <div className="mt-3 text-center" role="status" aria-live="polite">
                                <div style={{ color: '#5b21b6', fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.95rem' }}>
                                    Identity Verifying<span className="kp-iden-dots" />
                                </div>
                                <div style={{ height: 8, background: '#ede9fe', borderRadius: 9999, overflow: 'hidden', position: 'relative' }}>
                                    <div
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            height: '100%',
                                            width: '40%',
                                            borderRadius: 9999,
                                            background: 'linear-gradient(90deg,#a78bfa,#7c3aed,#5b21b6)',
                                            animation: 'kpIdenBar 1.15s cubic-bezier(0.65,0,0.35,1) infinite',
                                        }}
                                    />
                                </div>
                                <div style={{ color: '#8b5cf6', fontSize: '0.78rem', marginTop: '0.35rem' }}>
                                    Securely verifying your credentials…
                                </div>
                            </div>
                        )}
                    </form>

                    <div className="text-center mt-3">
                        <Link to="/forgot-password" style={{ fontSize: '0.9rem', color: '#7c3aed' }}>Forgot password?</Link>
                    </div>

                    <div className="d-flex align-items-center text-muted my-3">
                        <hr className="flex-grow-1" /><span className="px-2 small">or</span><hr className="flex-grow-1" />
                    </div>
                    <GoogleAuthButton onAuthed={loginState} />

                    <div className="text-center mt-3 small text-muted">
                        New to Kobra Prep? <Link to="/signup" style={{ color: '#7c3aed', fontWeight: 600 }}>Create an account</Link>
                    </div>
                </div>
            </div>

            <style>
                {`
                @keyframes kpIdenBar {
                    0%   { left: -40%; }
                    100% { left: 100%; }
                }
                @keyframes kpIdenDots {
                    0%   { content: ''; }
                    25%  { content: '.'; }
                    50%  { content: '..'; }
                    75%  { content: '...'; }
                    100% { content: ''; }
                }
                .kp-iden-dots::after {
                    content: '';
                    animation: kpIdenDots 1.4s steps(1, end) infinite;
                }
                `}
            </style>
        </div>
    );
}
